import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ArrowLeft, ExternalLink } from "lucide-react";

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function ActualiteDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api(`/actualites/${slug}`).then(setArticle).catch((e) => setError(e.message));
  }, [slug]);

  return (
    <AppLayout>
      <div data-testid="news-detail-page" className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        <Link to="/actualites" data-testid="news-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-cfhtb-secondary hover:text-cfhtb-primary transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour aux actualités
        </Link>

        {error ? (
          <div className="bg-white rounded-lg border border-cfhtb-error/30 p-6 text-sm text-cfhtb-error">{error}</div>
        ) : !article ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <article className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden">
            {article.image && <img src={article.image} alt="" className="w-full h-auto max-h-[70vh] object-contain bg-cfhtb-bg" />}
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap items-center gap-3 text-xs text-cfhtb-text">
                <span className="font-medium text-cfhtb-secondary">{formatDate(article.date)}</span>
                {article.categories.map((c) => (
                  <span key={c} className="bg-cfhtb-bg text-cfhtb-primary px-2 py-0.5 rounded-full font-medium">{c}</span>
                ))}
              </div>
              <h1
                data-testid="news-detail-title"
                className="font-serif text-3xl sm:text-4xl font-bold text-cfhtb-heading tracking-tight mt-3 leading-tight"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.titre) }}
              />
              <div
                data-testid="news-detail-content"
                className="mt-6 text-cfhtb-text leading-relaxed [&_p]:mb-4 [&_h2]:font-serif [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-cfhtb-heading [&_h2]:mt-6 [&_h3]:font-serif [&_h3]:text-lg [&_h3]:mt-5 [&_a]:text-cfhtb-secondary [&_a]:underline [&_img]:rounded-lg [&_img]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_blockquote]:border-l-4 [&_blockquote]:border-cfhtb-light [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.contenu) }}
              />
              {article.lien && (
                <a
                  href={article.lien}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="news-public-link"
                  className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 hover:bg-cfhtb-bg transition-colors"
                >
                  Lire sur cfhtb.org <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </article>
        )}
      </div>
    </AppLayout>
  );
}
